/**
 * Brew day's whirlpool temperatures (Scott's Jam, 2026-10-08: "whirlpool temps need to be manually
 * recorded based upon brew day conditions"). A recipe gives each whirlpool hop a temperature and a
 * steep, but how hot the wort is when the hops go in depends on the day, and it moves their bitterness a
 * lot: by `whirlpoolUtilization`, 180°F gives half again what 170°F does. Brew day takes a temperature for
 * each step, and a step left empty takes the first step's (Tyler, 2026-10-19: "1 per step but default to
 * the first step for the remaining, it can just be adjusted if needed").
 */

/** A whirlpool hop as its step needs it: the recipe's temperature, °C (null when it gives none), and steep. */
export interface WhirlpoolLine {
  id: string;
  tempC: number | null;
  minutes: number;
}

/** A hop the recipe gives no temperature is reckoned at boiling, as its bitterness is. */
const heatOf = (line: WhirlpoolLine) => line.tempC ?? 100;

/**
 * A recipe's whirlpool hops in steps, each step the hops that go in together: one temperature, one steep.
 * Hottest first, as the wort cools, and at one temperature the longer steep first, since it goes in
 * sooner. Within a step, the recipe's order.
 */
export function whirlpoolSteps<L extends WhirlpoolLine>(lines: readonly L[]): L[][] {
  const steps: L[][] = [];
  for (const line of lines) {
    const step = steps.find(
      ([first]) => first && heatOf(first) === heatOf(line) && first.minutes === line.minutes,
    );
    if (step) step.push(line);
    else steps.push([line]);
  }
  return steps.sort(([a], [b]) => (a && b ? heatOf(b) - heatOf(a) || b.minutes - a.minutes : 0));
}

/**
 * The temperature each whirlpool hop went in at, °C by line, from those typed on brew day by line: its
 * step's, else the first step's. A hop with neither is left out, to be worked at the recipe's.
 */
export function whirlpoolTempsOnTheDay(
  steps: readonly (readonly WhirlpoolLine[])[],
  typed: ReadonlyMap<string, number>,
): Map<string, number> {
  const typedFor = (step: readonly WhirlpoolLine[]) =>
    step.map((l) => typed.get(l.id)).find((t) => t !== undefined);
  const first = steps[0] ? typedFor(steps[0]) : undefined;
  const temps = new Map<string, number>();
  for (const step of steps) {
    const t = typedFor(step) ?? first;
    if (t === undefined) continue;
    for (const line of step) temps.set(line.id, t);
  }
  return temps;
}
